import React from 'react';

import {
  browserHistory,
  IndexRedirect,
  IndexRoute,
  Redirect,
  Route,
  Router,
} from 'react-router';

import {APISettingsPage} from 'app/account/components/APISettingsPage';
import {BillingDetailPage} from 'app/account/components/BillingDetailPage';
import {WebhookSettingsPage} from 'app/account/components/WebhookSettingsPage';
import {CookieAuthChecker} from 'app/auth/components/CookieAuthChecker';
import {
  CreateDocBreadcrumb,
  createStaticTextBreadcrumb,
  DocNameBreadcrumb,
  DocVersionBreadcrumb,
  EditDocBreadcrumb,
} from 'app/components/general/breadcrumbs';
import {
  HomeDashboard,
  HomeDashboardBreadcrumb,
  HomeDashboardFrame,
} from 'app/components/general/DashboardPage/HomeDashboard';
import {
  HomeFolderDetailDashboard,
  NestedFolderBreadcrumb,
} from 'app/components/general/DashboardPage/HomeFolderDetailDashboard';
import {HomeFoldersDashboard} from 'app/components/general/DashboardPage/HomeFoldersDashboard';
import {AcceptanceSidebar} from 'app/components/general/DetailPage/LeftSidebar/AcceptanceSidebar';
import {ActivitySidebar} from 'app/components/general/DetailPage/LeftSidebar/ActivitySidebar';
import {PermissionsSidebar} from 'app/components/general/DetailPage/LeftSidebar/PermissionsSidebar';
import {
  PoliciesForProcedureLeftSidebar,
  PoliciesForProcessLeftSidebar,
} from 'app/components/general/DetailPage/LeftSidebar/PoliciesLeftSidebar';
import {ProceduresForPolicyLeftSidebar} from 'app/components/general/DetailPage/LeftSidebar/ProceduresLeftSidebar';
import {
  ProcessesForPolicyLeftSidebar,
  ProcessesForProcedureLeftSidebar,
} from 'app/components/general/DetailPage/LeftSidebar/ProcessesLeftSidebar';
import {ReviewSidebar} from 'app/components/general/DetailPage/LeftSidebar/ReviewSidebar';
import {SignoffLogSidebar} from 'app/components/general/DetailPage/LeftSidebar/SignOffLogSidebar';
import {TasksSidebar} from 'app/components/general/DetailPage/LeftSidebar/TasksSidebar';
import {TeamsSidebar} from 'app/components/general/DetailPage/LeftSidebar/TeamsSidebar';
import {
  PolicyVersionsSidebar,
  ProcedureVersionsSidebar,
  ProcessVersionsSidebar,
} from 'app/components/general/DetailPage/LeftSidebar/VersionsSidebar/VersionsSidebar';
import {StickyComments} from 'app/components/general/DetailPage/RightSidebar/StickyDocumentCommentsSidebar';
import {HardRefreshToServer} from 'app/components/general/HardRefreshToServer';
import {SPBootstrapPage} from 'app/containers/general/SPBasePage';
import {DocumentFoldersBreadcrumbs} from 'app/documents/components/DocumentFoldersBreadcrumbs';
import {
  DocumentsDashboard,
  DocumentsDashboardBreadcrumb,
  TextLessDocumentsDashboardBreadcrumb,
} from 'app/documents/components/DocumentsDashboard';
import {NewDocumentPage} from 'app/documents/components/NewDocumentPage';
import {SPFrame, SPFrameWithRegularLinks} from 'app/general/structures/SPFrame';
import {SwitchAccountPage} from 'app/general/SwitchAccountPage';
import {WrapStaticContentPage} from 'app/general/WrapStaticContentPage';
import {KBEditPage} from 'app/kbs/components/KBEditPage';
import {
  KBsDashboardBreadcrumb,
  KnowledgebasesDashboard,
} from 'app/kbs/components/KnowledgebasesDashboard';
import {NewKnowledgebasePage} from 'app/kbs/components/NewKnowledgebasePage';
import {TemplateDetailPage} from 'app/market/components/TemplateDetailPage';
import {TemplatePolicyDetail} from 'app/market/components/TemplatePolicyDetail';
import {TemplateProcedureDetail} from 'app/market/components/TemplateProcedureDetail';
import {TemplateProcessDetail} from 'app/market/components/TemplateProcessDetail';
import {
  TemplatesDashboard,
  TemplatesDashboardBreadcrumb,
} from 'app/market/components/TemplatesDashboard';
import {
  NotificationsPage,
  NotificationsPageBreadcrumb,
} from 'app/notifly/components/NotificationsPage';
import {OverviewPage} from 'app/overview/components/OverviewPage';
import {NewPolicyPage} from 'app/policies/components/NewPolicyPage';
import {
  PoliciesDashboard,
  PoliciesDashboardBreadcrumb,
} from 'app/policies/components/PoliciesDashboard';
import PolicyDetailPage from 'app/policies/components/PolicyDetailPage';
import {LoadablePolicyEditPage} from 'app/policies/components/PolicyEditPage/LoadablePolicyEditPage';
import {NewProcedurePage} from 'app/procedures/components/NewProcedurePage';
import ProcedureDetailPage, {
  MaybeProcedureProcessContainerBreadcrumb,
} from 'app/procedures/components/ProcedureDetailPage';
import {ProcedureTaskReportPage} from 'app/procedures/components/ProcedureDetailPage/ProcedureTaskReportPage';
import {LoadableProcedureEditPage} from 'app/procedures/components/ProcedureEditPage/LoadableProcedureEditPage';
import {
  ProceduresDashboard,
  ProceduresDashboardBreadcrumb,
} from 'app/procedures/components/ProceduresDashboard';
import {LoadableProcessEditPage} from 'app/process/components/LoadableProcessEditPage';
import {ProcessCreatePage} from 'app/process/components/ProcessCreatePage';
import {
  ProcessesDashboard,
  ProcessesDashboardBreadcrumb,
} from 'app/process/components/ProcessesDashboard';
import {ProcessPage} from 'app/process/components/ProcessPage';
import {ProcessTaskReportPage} from 'app/process/components/ProcessTaskReportPage';
import {AttemptQuizPage} from 'app/quizzes/components/AttemptQuizPage/AttemptQuizPage';
import {AttemptResultPage} from 'app/quizzes/components/AttemptResultPage/AttemptResultPage';
import {NewQuizPage} from 'app/quizzes/components/NewQuizPage/NewQuizPage';
import {QuizPage} from 'app/quizzes/components/QuizDetailPage';
import {LoadableQuizEditPage} from 'app/quizzes/components/QuizEditPage/LoadableQuizEditPage';
import {
  QuizzesDashboard,
  QuizzesDashboardBreadcrumb,
} from 'app/quizzes/components/QuizzesDashboard/QuizzesDashboard';
import {RedirectToTakeQuizPage} from 'app/quizzes/components/TakeQuizPage/RedirectToTakeQuizPage';
import {RecycleBinPage} from 'app/recycle-bin/components/RecycleBinPage';
import {ReferralsPage} from 'app/referrals/components/ReferralsPage';
import {ReportsDetailPage} from 'app/reports/components/ReportsDetailPage';
import {
  ReportsBreadcrumb,
  ReportsPage,
} from 'app/reports/components/ReportsPage';
import {
  ReportsResultBreadcrumb,
  ReportsRunPage,
} from 'app/reports/components/ReportsRunPage';
import {SCIMBreadcrumb, SCIMPage} from 'app/scim/components/SCIMPage';
import {
  SearchBreadcrumb,
  SearchDashboard,
} from 'app/search/components/SearchDashboard';
import {
  TagsDashboard,
  TagsDashboardBreadcrumb,
} from 'app/tags/components/TagsDashboard';
import {CreateTaskInstancePage} from 'app/tasks/components/CreateTaskInstancePage';
import {
  InnerTaskDetailBreadcrumb,
  TaskDetailPage,
} from 'app/tasks/components/ProcedureTaskPage';
import {ProcessTaskPage} from 'app/tasks/components/ProcessTaskPage';
import {
  RedirecToLastUsedTasksDashboard,
  TasksDashboard,
  TasksPageBreadcrumb,
} from 'app/tasks/components/TasksDashboard';
import {TeamCreatePage} from 'app/teams/components/TeamCreatePage';
import {TeamDetailPage} from 'app/teams/components/TeamDetailPage';
import {
  TeamsDashboard,
  TeamsDashboardBreadcrumb,
} from 'app/teams/components/TeamsDashboard';
import {
  UserCreateBreadcrumb,
  UserCreatePage,
} from 'app/user/components/UserCreatePage';
import {UserDetailPage} from 'app/user/components/UserDetailPage';
import {ActivityList} from 'app/user/components/UserDetailPage/ActivityList';
import {InvitationList} from 'app/user/components/UserDetailPage/InvitationList';
import {PoliciesList} from 'app/user/components/UserDetailPage/PoliciesList';
import {ProcedureList} from 'app/user/components/UserDetailPage/ProcedureList';
import {ProcessList} from 'app/user/components/UserDetailPage/ProcessList';
import {TeamList} from 'app/user/components/UserDetailPage/TeamList';
import {UserSignoffsList} from 'app/user/components/UserDetailPage/UserSignoffsList';
import {
  UsersDashboard,
  UsersDashboardBreadcrumb,
} from 'app/user/components/UsersDashboard';
import {gt} from 'app/utils/i18n';
import {
  ForwardToPolicyEditPage,
  ForwardToProcedureEditPage,
  ForwardToProcessEditPage,
  ForwardToQuizEditPage,
} from 'app/version/components/ForwardToDocumentEditPage';
import {ImportDashboard} from 'app/welcome/components/ImportDashboard';
import {InviteDashboard} from 'app/welcome/components/InviteDashboard';
import {WelcomeDashboard} from 'app/welcome/components/WelcomeDashboard';

import type {DetailPageRouteProps} from 'app/components/general/DetailPage/types';
import type {
  DetailPolicySerializedType,
  ProcedureHashidSerializedType,
  ProcessHashidSerializedType,
} from 'sweet-types';

export const RootRouter = () => (
  <Router history={browserHistory}>
    <Route component={CookieAuthChecker}>
      <Route component={SPBootstrapPage}>
        <Route path="/home/" component={SPFrame}>
          <Route
            component={HomeDashboardFrame}
            props={{breadcrumb: HomeDashboardBreadcrumb}}
          >
            <Route path="">
              <IndexRoute component={HomeFoldersDashboard} />
              <Route
                path=":name/:hashid/"
                component={HomeFolderDetailDashboard}
                props={{breadcrumb: NestedFolderBreadcrumb}}
              />
            </Route>
            <Route
              path="todos/"
              component={HomeDashboard}
              props={{
                breadcrumb: createStaticTextBreadcrumb(gt('Action Center')),
              }}
            />
          </Route>
        </Route>
        <Route
          props={{
            breadcrumb: TextLessDocumentsDashboardBreadcrumb,
          }}
        >
          <Route
            props={{
              breadcrumb: DocNameBreadcrumb,
            }}
            path="documents/welcome/"
            component={ImportDashboard}
          />
          <Route
            props={{
              breadcrumb: DocNameBreadcrumb,
            }}
            path="documents/import/"
            component={WelcomeDashboard}
          />
          <Route
            props={{
              breadcrumb: DocNameBreadcrumb,
            }}
            path="documents/invite/"
            component={InviteDashboard}
          />
        </Route>

        <Route component={SPFrameWithRegularLinks}>
          <Route
            path="/accounts/stripe/success/*"
            component={BillingDetailPage}
          />
        </Route>

        <Route component={SPFrame}>
          <Route
            props={{
              breadcrumb: TextLessDocumentsDashboardBreadcrumb,
            }}
          >
            <Route
              path="/documents/"
              props={{
                activateNavLink: 'documents',
                breadcrumb: DocumentsDashboardBreadcrumb,
              }}
            >
              <IndexRoute
                component={DocumentsDashboard}
                props={{
                  breadcrumb: DocumentFoldersBreadcrumbs,
                }}
              />
              <Route
                path="new/"
                component={NewDocumentPage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
            </Route>
            <Route
              path="/procedures/"
              props={{
                activateNavLink: 'procedures',
                breadcrumb: ProceduresDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={ProceduresDashboard} />
              <Route
                path="new/"
                component={NewProcedurePage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route
                props={{
                  breadcrumb: MaybeProcedureProcessContainerBreadcrumb,
                }}
              >
                <Route
                  path=":hashid/(:slug)/"
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={ProcedureDetailPage} props={{}} />
                  <Route
                    path="edit/"
                    component={ForwardToProcedureEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route
                    path="version/:versionid"
                    props={{
                      breadcrumb: DocVersionBreadcrumb,
                    }}
                  >
                    <IndexRoute
                      component={ProcedureDetailPage}
                      props={
                        {
                          CurrentLeftSidebar: ProcedureVersionsSidebar,
                        } as DetailPageRouteProps<ProcedureHashidSerializedType>
                      }
                    />
                    <Route
                      path="comment/"
                      component={ProcedureDetailPage}
                      props={
                        {
                          CurrentRightSidebar: StickyComments,
                        } as DetailPageRouteProps<ProcedureHashidSerializedType>
                      }
                    />
                    <Route
                      path="accept/"
                      component={ProcedureDetailPage}
                      props={
                        {
                          CurrentLeftSidebar: AcceptanceSidebar,
                        } as DetailPageRouteProps<ProcedureHashidSerializedType>
                      }
                    />
                    <Route
                      path="edit/"
                      component={LoadableProcedureEditPage}
                      props={{
                        breadcrumb: EditDocBreadcrumb,
                      }}
                    />
                  </Route>
                  <Route path="tasks/">
                    <IndexRoute
                      component={ProcedureDetailPage}
                      props={
                        {
                          CurrentLeftSidebar: TasksSidebar,
                        } as DetailPageRouteProps<ProcedureHashidSerializedType>
                      }
                    />
                    <Route path="report/">
                      <IndexRoute component={ProcedureTaskReportPage} />
                      <Route
                        path="(:reportid)/"
                        component={ProcedureTaskReportPage}
                      />
                    </Route>
                  </Route>
                  <Route
                    path="activity/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: ActivitySidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                  <Route
                    path="review/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: ReviewSidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                  <Route
                    path="signoff/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: SignoffLogSidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                  <Route
                    path="policies/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: PoliciesForProcedureLeftSidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                  <Route
                    path="processes/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: ProcessesForProcedureLeftSidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                  <Route
                    path="teams/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: TeamsSidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                  <Route
                    path="permissions/"
                    component={ProcedureDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: PermissionsSidebar,
                      } as DetailPageRouteProps<ProcedureHashidSerializedType>
                    }
                  />
                </Route>
              </Route>
            </Route>

            <Route
              path="/tags/:tag"
              component={ProceduresDashboard}
              props={{
                activateNavLink: 'procedures',
                breadcrumb: ProceduresDashboardBreadcrumb,
              }}
            />

            <Redirect from="/folders/" to="documents/" />
            <Redirect from="/folders/:hashid/" to="documents/?folder=:hashid" />

            <Route
              path="/tags/"
              component={TagsDashboard}
              props={{
                activateNavLink: 'tags',
                breadcrumb: TagsDashboardBreadcrumb,
              }}
            />
            <Route
              path="/search/"
              props={{
                breadcrumb: SearchBreadcrumb,
              }}
              component={SearchDashboard}
            />

            <Route
              props={{
                activateNavLink: 'tasks',
                breadcrumb: TasksPageBreadcrumb,
              }}
            >
              <Route
                path="/procedures/:hashid/(:slug)/task/:taskid/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={TaskDetailPage} props={{}} />
                <Route
                  path="process/:processid/"
                  component={TaskDetailPage}
                  props={{
                    breadcrumb: InnerTaskDetailBreadcrumb,
                  }}
                />
                <Route
                  path="activity/"
                  component={TaskDetailPage}
                  props={{
                    showActivity: true,
                  }}
                />
                <Route
                  path="comments/"
                  component={TaskDetailPage}
                  props={{
                    showComments: true,
                  }}
                />
                <Route
                  path="tasks/"
                  component={TaskDetailPage}
                  props={{
                    showTasks: true,
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/groups/"
              props={{
                activateNavLink: 'processes',
                breadcrumb: ProcessesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={ProcessesDashboard} />
              <Route
                path="add/"
                component={ProcessCreatePage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route path=":hashid/(:slug)/">
                <Route
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={ProcessPage} props={{}} />
                  <Route
                    path="edit/"
                    component={ForwardToProcessEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route path="tasks/">
                    <IndexRoute
                      component={ProcessPage}
                      props={
                        {
                          CurrentLeftSidebar: TasksSidebar,
                        } as DetailPageRouteProps<ProcessHashidSerializedType>
                      }
                    />
                    <Route path="report/">
                      <IndexRoute component={ProcessTaskReportPage} />
                      <Route
                        path="(:reportid)/"
                        component={ProcessTaskReportPage}
                      />
                    </Route>
                  </Route>
                  <Route
                    path="policies/"
                    component={ProcessPage}
                    props={
                      {
                        CurrentLeftSidebar: PoliciesForProcessLeftSidebar,
                      } as DetailPageRouteProps<ProcessHashidSerializedType>
                    }
                  />
                  <Route
                    path="teams/"
                    component={ProcessPage}
                    props={
                      {
                        CurrentLeftSidebar: TeamsSidebar,
                      } as DetailPageRouteProps<ProcessHashidSerializedType>
                    }
                  />
                  <Route
                    path="permissions/"
                    component={ProcessPage}
                    props={
                      {
                        CurrentLeftSidebar: PermissionsSidebar,
                      } as DetailPageRouteProps<ProcessHashidSerializedType>
                    }
                  />
                  <Route
                    path="review/"
                    component={ProcessPage}
                    props={
                      {
                        CurrentLeftSidebar: ReviewSidebar,
                      } as DetailPageRouteProps<ProcessHashidSerializedType>
                    }
                  />
                  <Route
                    path="activity/"
                    component={ProcessPage}
                    props={
                      {
                        CurrentLeftSidebar: ActivitySidebar,
                      } as DetailPageRouteProps<ProcessHashidSerializedType>
                    }
                  />
                  <Route
                    path="signoff/"
                    component={ProcessPage}
                    props={
                      {
                        CurrentLeftSidebar: SignoffLogSidebar,
                      } as DetailPageRouteProps<ProcessHashidSerializedType>
                    }
                  />
                  <Route
                    path="version/:versionid"
                    props={{
                      breadcrumb: DocVersionBreadcrumb,
                    }}
                  >
                    <IndexRoute
                      component={ProcessPage}
                      props={
                        {
                          CurrentLeftSidebar: ProcessVersionsSidebar,
                        } as DetailPageRouteProps<ProcessHashidSerializedType>
                      }
                    />
                    <Route
                      path="accept/"
                      component={ProcessPage}
                      props={
                        {
                          CurrentLeftSidebar: AcceptanceSidebar,
                        } as DetailPageRouteProps<ProcessHashidSerializedType>
                      }
                    />
                    <Route
                      path="comment/"
                      component={ProcessPage}
                      props={
                        {
                          CurrentRightSidebar: StickyComments,
                        } as DetailPageRouteProps<ProcessHashidSerializedType>
                      }
                    />
                    <Route
                      path="edit/"
                      component={LoadableProcessEditPage}
                      props={{
                        breadcrumb: EditDocBreadcrumb,
                      }}
                    />
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route
              props={{
                activateNavLink: 'tasks',
                breadcrumb: TasksPageBreadcrumb,
              }}
            >
              <Route
                path="/groups/:hashid/(:slug)/task/:taskid/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={ProcessTaskPage} props={{}} />
                <Route
                  path="activity/"
                  component={ProcessTaskPage}
                  props={{
                    showActivity: true,
                  }}
                />
                <Route
                  path="comments/"
                  component={ProcessTaskPage}
                  props={{
                    showComments: true,
                  }}
                />
                <Route
                  path="tasks/"
                  component={ProcessTaskPage}
                  props={{
                    showTasks: true,
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/policy"
              props={{
                activateNavLink: 'policies',
                breadcrumb: PoliciesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={PoliciesDashboard} />
              <Route
                path="new/"
                component={NewPolicyPage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route
                path=":hashid/(:slug)/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={PolicyDetailPage} props={{}} />
                <Route
                  path="edit/"
                  component={ForwardToPolicyEditPage}
                  props={{
                    breadcrumb: EditDocBreadcrumb,
                  }}
                />
                <Route
                  path="activity/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: ActivitySidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="teams/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: TeamsSidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="permissions/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: PermissionsSidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="procedures/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: ProceduresForPolicyLeftSidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="processes/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: ProcessesForPolicyLeftSidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="review/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: ReviewSidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="signoff/"
                  component={PolicyDetailPage}
                  props={
                    {
                      CurrentLeftSidebar: SignoffLogSidebar,
                    } as DetailPageRouteProps<DetailPolicySerializedType>
                  }
                />
                <Route
                  path="version/:versionid/"
                  props={{
                    breadcrumb: DocVersionBreadcrumb,
                  }}
                >
                  <IndexRoute
                    component={PolicyDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: PolicyVersionsSidebar,
                      } as DetailPageRouteProps<DetailPolicySerializedType>
                    }
                  />
                  <Route
                    path="accept/"
                    component={PolicyDetailPage}
                    props={
                      {
                        CurrentLeftSidebar: AcceptanceSidebar,
                      } as DetailPageRouteProps<DetailPolicySerializedType>
                    }
                  />
                  <Route
                    path="edit/"
                    component={LoadablePolicyEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route path="accept/" component={PolicyDetailPage} />
                  <Route
                    path="comment/"
                    component={PolicyDetailPage}
                    props={
                      {
                        CurrentRightSidebar: StickyComments,
                      } as DetailPageRouteProps<DetailPolicySerializedType>
                    }
                  />
                </Route>
              </Route>
            </Route>

            <Route
              path="/market/templates/"
              props={{
                activateNavLink: 'templates',
                breadcrumb: TemplatesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={TemplatesDashboard} props={{}} />
              <Route component={TemplateDetailPage}>
                <Route
                  path="policy/:hashid/(:slug)/"
                  component={TemplatePolicyDetail}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                />
                <Route
                  path="procedure/:hashid/(:slug)/"
                  component={TemplateProcedureDetail}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                />
                <Route
                  path="proceduregroup/:hashid/(:slug)/"
                  component={TemplateProcessDetail}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/quiz"
              props={{
                activateNavLink: 'quizzes',
                breadcrumb: QuizzesDashboardBreadcrumb,
              }}
            >
              <IndexRoute component={QuizzesDashboard} />
              <Route
                path="new/"
                component={NewQuizPage}
                props={{
                  breadcrumb: CreateDocBreadcrumb,
                }}
              />
              <Route
                path="take/:hashid/(:slug)/"
                props={{breadcrumb: DocNameBreadcrumb}}
              >
                <IndexRoute component={RedirectToTakeQuizPage} />
                <Route path="take/:attemptid/">
                  <IndexRoute component={AttemptQuizPage} />
                  <Route path="result" component={AttemptResultPage} />
                </Route>
              </Route>

              <Route
                path=":hashid/(:slug)/"
                props={{
                  breadcrumb: DocNameBreadcrumb,
                }}
              >
                <IndexRoute component={QuizPage} props={{}} />
                <Route
                  path="edit/"
                  component={ForwardToQuizEditPage}
                  props={{
                    breadcrumb: EditDocBreadcrumb,
                  }}
                />
                <Route
                  path="teams/"
                  component={QuizPage}
                  props={{
                    showTeams: true,
                  }}
                />
                <Route
                  path="procedures/"
                  component={QuizPage}
                  props={{
                    showProcedures: true,
                  }}
                />
                <Route
                  path="policies/"
                  component={QuizPage}
                  props={{
                    showPolicies: true,
                  }}
                />
                <Route
                  path="processes/"
                  component={QuizPage}
                  props={{
                    showProcesses: true,
                  }}
                />
                <Route
                  path="attempts/"
                  component={QuizPage}
                  props={{
                    showAttempts: true,
                  }}
                />
                <Route
                  path="version/:versionid/"
                  props={{
                    breadcrumb: DocVersionBreadcrumb,
                  }}
                >
                  <IndexRoute
                    component={QuizPage}
                    props={{
                      showVersions: true,
                    }}
                  />
                  <Route
                    path="accept/"
                    component={QuizPage}
                    props={{
                      showAccept: true,
                    }}
                  />
                  <Route
                    path="edit/"
                    component={LoadableQuizEditPage}
                    props={{
                      breadcrumb: EditDocBreadcrumb,
                    }}
                  />
                  <Route path="accept/" component={QuizPage} />
                </Route>
              </Route>
            </Route>

            <Route path="/people">
              <Route
                props={{
                  activateNavLink: 'teams',
                  breadcrumb: TeamsDashboardBreadcrumb,
                }}
              >
                <IndexRoute component={TeamsDashboard} />
                <Route
                  path="add-team/"
                  component={TeamCreatePage}
                  props={{
                    breadcrumb: CreateDocBreadcrumb,
                  }}
                />
                <Route
                  path="team/:teamid/:slug/"
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={TeamDetailPage} />
                  <Route path="users/" component={TeamDetailPage} />
                  <Route path="processes/" component={TeamDetailPage} />
                  <Route path="procedures/" component={TeamDetailPage} />
                  <Route path="policies/" component={TeamDetailPage} />
                  <Route path="folders/" component={TeamDetailPage} />
                  <Route path="signoffs/" component={TeamDetailPage} />
                  <Route path="products/" component={TeamDetailPage} />
                  <Route path="roles/" component={TeamDetailPage} />
                  <Route path="quizzes/" component={TeamDetailPage} />
                </Route>
              </Route>

              <Route
                path="users/"
                props={{
                  activateNavLink: 'members',
                  breadcrumb: UsersDashboardBreadcrumb,
                }}
              >
                <IndexRoute component={UsersDashboard} />
                <Route path="add">
                  <IndexRoute
                    component={UserCreatePage}
                    props={{
                      breadcrumb: UserCreateBreadcrumb,
                    }}
                  />
                </Route>
                <Route
                  path=":userid/"
                  component={UserDetailPage}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRedirect to="invites/" />
                  <Route path="invites/" component={InvitationList} />
                  <Route path="teams/" component={TeamList} />
                  <Route path="activity/" component={ActivityList} />
                  <Route path="procedures/" component={ProcedureList} />
                  <Route path="processes/" component={ProcessList} />
                  <Route path="policies/" component={PoliciesList} />
                  <Route path="signoffs/" component={UserSignoffsList} />
                </Route>
              </Route>
            </Route>

            <Route path="/accounts/">
              <Route
                path="notifications"
                component={NotificationsPage}
                props={{
                  breadcrumb: NotificationsPageBreadcrumb,
                }}
              />
              <Route
                path="overview/"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(
                    gt('Account Overview')
                  ),
                }}
                component={OverviewPage}
              />
              <Route
                path="switch"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(gt('Account Switch')),
                }}
                component={SwitchAccountPage}
              />
              <Route
                path="reports/"
                props={{
                  breadcrumb: ReportsBreadcrumb,
                }}
              >
                <IndexRoute component={ReportsPage} />
                <Route
                  path=":slug/"
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                >
                  <IndexRoute component={ReportsDetailPage} />
                  <Route
                    path=":runid/"
                    component={ReportsRunPage}
                    props={{
                      breadcrumb: ReportsResultBreadcrumb,
                    }}
                  />
                </Route>
              </Route>
              <Route
                path="scim"
                props={{breadcrumb: SCIMBreadcrumb}}
                component={SCIMPage}
              />
              <Route
                path="referrer"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(gt('Referrals')),
                }}
                component={ReferralsPage}
              />
              <Route
                path="recycle"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(gt('Recycle Bin')),
                }}
                component={RecycleBinPage}
              />
              <Route
                path="kb/"
                props={{
                  activateNavLink: 'kbase',
                  breadcrumb: KBsDashboardBreadcrumb,
                }}
              >
                <IndexRoute component={KnowledgebasesDashboard} />
                <Route
                  path="new/"
                  component={NewKnowledgebasePage}
                  props={{
                    breadcrumb: CreateDocBreadcrumb,
                  }}
                />
                <Route
                  path="edit/:hashid/"
                  component={KBEditPage}
                  props={{
                    breadcrumb: DocNameBreadcrumb,
                  }}
                />
              </Route>
              <Route
                path="tokens/*"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(gt('API Settings')),
                }}
                component={APISettingsPage}
              />
              <Route
                path="webhooks/*"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(gt('Webhooks')),
                }}
                component={WebhookSettingsPage}
              />
              <Route
                path="stripe/detail/*"
                props={{
                  breadcrumb: createStaticTextBreadcrumb(
                    gt('Billing Overview')
                  ),
                }}
                component={BillingDetailPage}
              />
              <Route component={WrapStaticContentPage}>
                <Route
                  path="settings/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Account Overview')
                    ),
                  }}
                />
                <Route
                  path="b/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Add New Members in Bulk')
                    ),
                  }}
                />
                <Route
                  path="emailsettings/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Notification Settings')
                    ),
                  }}
                />
                <Route
                  path="two-factor-auth/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(gt('Security')),
                  }}
                />
                <Route
                  path="password_change/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Change Password')
                    ),
                  }}
                />
                <Route
                  path="billing/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('My Subscription')
                    ),
                  }}
                />
                <Route
                  path="stripe/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('My Stripe Subscription')
                    ),
                  }}
                />
                <Route
                  path="migration/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Migration Service')
                    ),
                  }}
                />
                <Route
                  path="recycle/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(gt('Recycle')),
                  }}
                />
                <Route
                  path="export/pdf/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(gt('Export')),
                  }}
                />
                <Route
                  path="createaccount/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Create Account')
                    ),
                  }}
                />
                <Route
                  path="knowledgebase/*"
                  props={{
                    breadcrumb: createStaticTextBreadcrumb(
                      gt('Knowledge Base')
                    ),
                  }}
                />
              </Route>
            </Route>

            <Route
              path="/help/*"
              component={WrapStaticContentPage}
              props={{
                breadcrumb: createStaticTextBreadcrumb(gt('Help')),
              }}
            />

            <Route
              path="/tasks/"
              props={{
                activateNavLink: 'tasks',
                breadcrumb: TasksPageBreadcrumb,
              }}
            >
              <IndexRoute component={RedirecToLastUsedTasksDashboard} />
              <Route path="all/" component={TasksDashboard} />
              <Route path="mine/" component={TasksDashboard} />
              <Route
                path="do/:slug/:taskid/:timestamp(/:subnav)/"
                component={CreateTaskInstancePage}
              />
            </Route>
            <Route path="/*" component={HardRefreshToServer} />
          </Route>
        </Route>
      </Route>
    </Route>
  </Router>
);
